import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { PennButton, PennText } from "@/components/ui";
import { SupportModal } from "@/components/popup/SupportModal";
import { trackEvent } from "@penn/web-common";
import { useAppSelector } from "@/stores/hooks";

/**
 * Routes that may show the SupportAssistant ("Need help?" floating card +
 * Request Support modal). Anywhere else (landing, login, register, etc.) it
 * stays hidden — the assistant is a Selector-page affordance only.
 */
const SUPPORTED_PATHS: ReadonlySet<string> = new Set(["/installation-system"]);

const DISMISSED_KEY = "support_assistant_dismissed";
const SHOWN_KEY = "support_assistant_shown";
// POC parity: 2 min without progress + 20 s without interaction.
const PROGRESS_THRESHOLD_MS = 120_000;
const IDLE_THRESHOLD_MS = 20_000;
const POLL_INTERVAL_MS = 5_000;

/**
 * Custom event other parts of the app dispatch when the user makes
 * meaningful progress (e.g., adds an application, selects a solution).
 * The assistant resets its progress timer on receipt.
 */
export const SUPPORT_ASSISTANT_PROGRESS_EVENT = "supportassistant:progress";

export const dispatchSupportAssistantProgress = (reason?: string) => {
  trackEvent("progress_made", { reason: reason ?? null });
  window.dispatchEvent(new CustomEvent(SUPPORT_ASSISTANT_PROGRESS_EVENT));
};

/**
 * Dev affordance: visiting any URL with `?supportAssistant=force` immediately
 * shows the assistant and clears the per-session shown/dismissed flags so it
 * can be retested without waiting out the 2 min/20 s timers.
 */
const isForceMode = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("supportAssistant") === "force";
  } catch {
    return false;
  }
};

export const SupportAssistant = () => {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const role = useAppSelector((s) => s.auth.user?.role);
  const location = useLocation();
  const navigate = useNavigate();
  const onSupportedRoute = SUPPORTED_PATHS.has(location.pathname);

  const [visible, setVisible] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);

  const lastInteractionRef = useRef<number>(Date.now());
  const lastProgressRef = useRef<number>(Date.now());

  // Force mode bypasses every gate (internal-user check + sessionStorage flags
  // + idle thresholds) so the assistant can be QAed without juggling roles.
  const forceMode = isForceMode();

  // Internal users never see the helper (POC parity), unless force mode is on.
  const isInternalUser = (() => {
    if (forceMode) return false;
    if (!isAuthenticated || !role) return false;
    return (
      !!role.canSeePrices ||
      !!role.canGenerateQuote ||
      !!role.canManageUsers ||
      !!role.canApproveEngineering ||
      !!role.isInternal
    );
  })();

  const hide = useCallback((persistDismiss: boolean) => {
    setVisible(false);
    if (persistDismiss) {
      try {
        sessionStorage.setItem(DISMISSED_KEY, "1");
      } catch {
        // ignore
      }
    }
  }, []);

  const show = useCallback(() => {
    if (isInternalUser) return;
    try {
      if (sessionStorage.getItem(DISMISSED_KEY) === "1") return;
      if (sessionStorage.getItem(SHOWN_KEY) === "1") return;
      sessionStorage.setItem(SHOWN_KEY, "1");
    } catch {
      // ignore
    }
    trackEvent("support_assistant_shown", {
      msSinceLastProgress: Date.now() - lastProgressRef.current,
    });
    setVisible(true);
  }, [isInternalUser]);

  // ?supportAssistant=force — clear session flags + show immediately.
  useEffect(() => {
    if (!isForceMode()) return;
    try {
      sessionStorage.removeItem(DISMISSED_KEY);
      sessionStorage.removeItem(SHOWN_KEY);
    } catch {
      // ignore
    }
    if (!isInternalUser) setVisible(true);
  }, [isInternalUser]);

  // Track interaction (any click/key/touch resets the idle clock).
  useEffect(() => {
    const markInteraction = () => {
      lastInteractionRef.current = Date.now();
    };
    document.addEventListener("click", markInteraction, true);
    document.addEventListener("keydown", markInteraction, true);
    document.addEventListener("touchstart", markInteraction, {
      passive: true,
      capture: true,
    });
    return () => {
      document.removeEventListener("click", markInteraction, true);
      document.removeEventListener("keydown", markInteraction, true);
      document.removeEventListener("touchstart", markInteraction, true);
    };
  }, []);

  // Track meaningful progress (apps added, machine picked, etc.).
  useEffect(() => {
    const onProgress = () => {
      lastProgressRef.current = Date.now();
    };
    window.addEventListener(SUPPORT_ASSISTANT_PROGRESS_EVENT, onProgress);
    return () => {
      window.removeEventListener(SUPPORT_ASSISTANT_PROGRESS_EVENT, onProgress);
    };
  }, []);

  // Poll the inactivity threshold.
  useEffect(() => {
    if (isInternalUser) return;
    // Only auto-surface on the Selector route.
    if (!onSupportedRoute) return;
    const id = window.setInterval(() => {
      if (visible) return;
      try {
        if (sessionStorage.getItem(DISMISSED_KEY) === "1") return;
      } catch {
        return;
      }
      // Reset progress clock if Request modal is open (POC behaviour).
      if (supportOpen) {
        lastProgressRef.current = Date.now();
        return;
      }
      const now = Date.now();
      const sinceProgress = now - lastProgressRef.current;
      const sinceInteraction = now - lastInteractionRef.current;
      if (
        sinceProgress >= PROGRESS_THRESHOLD_MS &&
        sinceInteraction >= IDLE_THRESHOLD_MS
      ) {
        show();
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isInternalUser, visible, supportOpen, show, onSupportedRoute]);

  // Drop the visible card immediately if the user navigates away from the
  // Selector route (e.g. clicks the logo to go to the landing page).
  useEffect(() => {
    if (!onSupportedRoute && visible) setVisible(false);
  }, [onSupportedRoute, visible]);

  // Internal users: render nothing.
  if (isInternalUser) return null;
  // The Need Help card + Request Support modal are Selector-only.
  if (!onSupportedRoute) return null;

  return (
    <>
      {visible && (
        <div className="fixed bottom-4 right-4 z-[60] max-w-sm">
          <div className="rounded-2xl shadow-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 flex items-start gap-3">
              <div className="mt-0.5 h-9 w-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700 font-semibold">
                ?
              </div>
              <div className="flex-1">
                <PennText as="p" size="sm" font="semibold" color="blue_grey1">
                  Need help?
                </PennText>
                <PennText as="p" size="sm" color="blue_grey" className="mt-0.5">
                  If you are not sure how to proceed, I can guide you to the next step.
                </PennText>
                {showHelpText && (
                  <div className="mt-2">
                    <PennText as="p" size="sm" color="blue_grey">
                      Suggested next steps:
                    </PennText>
                    <ul className="mt-1 list-disc pl-5 text-sm text-slate-600">
                      <li>Add an application / part</li>
                      <li>Select a solution</li>
                      <li>Submit a quotation/support request</li>
                    </ul>
                  </div>
                )}
              </div>
              <button
                type="button"
                title="Dismiss"
                onClick={() => {
                  trackEvent("support_assistant_dismissed", { via: "x" });
                  hide(true);
                }}
                className="h-8 w-8 rounded-lg hover:bg-slate-50 text-slate-500"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
            <div className="px-4 pb-4 flex flex-wrap gap-2">
              <PennButton
                variant="primary"
                size="sm"
                onClick={() => {
                  trackEvent("support_assistant_action", { action: "request_support" });
                  hide(true);
                  // Same gate as the Selector's own "Request Support" button —
                  // this opens the same RFQ modal, so it needs a signed-in user.
                  if (!isAuthenticated) {
                    navigate("/login", { state: { from: { pathname: location.pathname } } });
                    return;
                  }
                  setSupportOpen(true);
                }}
              >
                Request support / quote
              </PennButton>
              <PennButton
                variant="secondary"
                size="sm"
                onClick={() => {
                  trackEvent("support_assistant_action", { action: "how_it_works" });
                  setShowHelpText((v) => !v);
                }}
              >
                How it works
              </PennButton>
              <PennButton
                variant="secondary"
                size="sm"
                onClick={() => {
                  trackEvent("support_assistant_dismissed", { via: "button" });
                  hide(true);
                }}
              >
                Dismiss
              </PennButton>
            </div>
          </div>
        </div>
      )}

      <SupportModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
      />
    </>
  );
};
